/* eslint-disable */
import { faro } from '@grafana/faro-web-sdk';
import jwtDecode from 'jwt-decode';

import { ACCESS_TOKEN_ENDPOINT, getBaseUrl, LOGGED_IN_USER_INFO } from '../../constants';

import {
  getParsedValueFromLocalStorage,
  removeFromLocalStorage,
  setValueToLocalStorage,
} from '../../services/localStorage';
import { showPartialSuccessNotice } from '../../services/partialSuccessNotice';

import { HttpErrors } from './httpErrors';
import { requestHeaders } from './requestHeaders';

const MAX_REFRESH_RETRIES = 3;

const PARTIAL_CONTENT = 206;

const extractError = (data) => data?.error ?? data?.errors?.[0] ?? {};

/**
 * the API answers with 206 when the request went through but something around it did not,
 * and the reason tells the user what to do about it (e.g. the consent form mail failed and
 * has to be sent manually). The request itself stays successful.
 */
const notifyOnPartialContent = (status, results) => {
  if (status !== PARTIAL_CONTENT) return;

  const { reason, message } = extractError(results);
  showPartialSuccessNotice({ message: reason || message });
};

const normalizeMeta = (meta) => {
  if (!meta) return meta;
  const normalized = { ...meta };
  if (normalized.Total !== undefined && normalized.total === undefined) {
    normalized.total = normalized.Total;
    delete normalized.Total;
  }
  return normalized;
};
const BASE_BACKOFF_MS = 300;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export class Fetcher {
  static instance;
  static refreshPromise = null;

  static getInstance() {
    if (this.instance !== undefined) return this.instance;
    this.instance = new Fetcher();
    return this.instance;
  }

  baseRequest = async (url, options) => {
    try {
      return await fetch(url, options);
    } catch {
      throw new Error('Unable to connect to the internet. Please check your internet connection.');
    }
  };

  // ---------------- TENANT REFRESH (ORG SWITCH) ----------------

  tenantRefresh = async (payload) => {
    const response = await this.baseRequest(`${getBaseUrl()}${ACCESS_TOKEN_ENDPOINT}`, {
      method: 'POST',
      headers: requestHeaders(),
      body: JSON.stringify(payload),
    });

    let data = {};
    try {
      data = await response.json();
    } catch {
      throw new HttpErrors({ reason: 'Invalid refresh response' }, response.status);
    }

    if (response.status >= 400) {
      throw new HttpErrors(extractError(data), response.status);
    }

    const userData = getParsedValueFromLocalStorage(LOGGED_IN_USER_INFO);

    if (!data?.data?.accessToken) {
      throw new HttpErrors({ reason: 'Invalid token refresh response' }, 500);
    }

    setValueToLocalStorage(
      LOGGED_IN_USER_INFO,
      JSON.stringify({
        ...userData,
        accessToken: data?.data?.accessToken,
        refreshToken: data?.data?.refreshToken || userData?.refreshToken,
      })
    );

    return true;
  };

  // ---------------- STANDARD TOKEN REFRESH ----------------

  refreshToken = async (payload = '', retryCount = 0) => {
    const userData = getParsedValueFromLocalStorage(LOGGED_IN_USER_INFO);

    if (!userData?.refreshToken) {
      throw new HttpErrors({ message: 'No refresh token available' }, 401);
    }

    if (Fetcher.refreshPromise) {
      return Fetcher.refreshPromise;
    }

    Fetcher.refreshPromise = (async () => {
      try {
        let tenantID = '';

        try {
          const claims = jwtDecode(userData?.accessToken);
          tenantID = claims?.['tenant-id'] || '';
        } catch {
          tenantID = '';
        }

        const body =
          payload !== ''
            ? payload
            : {
                tenantID,
                refreshToken: userData.refreshToken,
              };

        const response = await fetch(`${getBaseUrl()}${ACCESS_TOKEN_ENDPOINT}`, {
          method: 'POST',
          headers: requestHeaders(),
          body: JSON.stringify(body),
        });

        let data = {};
        try {
          data = await response.json();
        } catch {
          throw new HttpErrors({ reason: 'Invalid refresh response' }, response.status);
        }

        if (response.status === 401 || response.status === 403) {
          throw new HttpErrors({ message: 'Refresh token expired or invalid' }, response.status);
        }

        if (response.status >= 400) {
          throw new HttpErrors(extractError(data), response.status);
        }

        if (!data?.data?.accessToken) {
          throw new HttpErrors({ reason: 'Invalid refresh token response' }, 500);
        }

        setValueToLocalStorage(
          LOGGED_IN_USER_INFO,
          JSON.stringify({
            ...userData,
            accessToken: data?.data?.accessToken,
          })
        );

        return true;
      } catch (error) {
        faro?.api?.pushError?.(error);

        if (error?.status === 401 || error?.status === 403) {
          throw error;
        }

        if (retryCount < MAX_REFRESH_RETRIES) {
          const delay = BASE_BACKOFF_MS * 2 ** retryCount;
          await sleep(delay);
          return this.refreshToken(payload, retryCount + 1);
        }

        throw error;
      } finally {
        Fetcher.refreshPromise = null;
      }
    })();

    return Fetcher.refreshPromise;
  };

  // ---------------- PUBLIC REQUEST ----------------

  sendPublicRequest = async (url, options = {}) => {
    const response = await this.baseRequest(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(options.headers || {}),
      },
    });

    let data = {};
    try {
      data = response.status !== 204 ? await response.json() : {};
    } catch {
      throw new HttpErrors({ reason: 'Invalid response' }, response.status);
    }

    if (response.status >= 400) {
      throw new HttpErrors(extractError(data), response.status);
    }

    const rawMeta = data.metadata ?? data.meta;
    return { data: data.data, meta: normalizeMeta(rawMeta), status: response.status };
  };

  // ---------------- PRIVATE REQUEST ----------------

  sendPrivateRequest = async (url, options = {}, retried = false) => {
    const response = await this.baseRequest(url, {
      ...options,
      headers: {
        ...(options.headers || {}),
        ...requestHeaders(),
      },
    });

    let results = {};
    if (response.status !== 204) {
      try {
        results = await response.json();
      } catch {
        throw new HttpErrors({ reason: 'Invalid response' }, response.status);
      }
    }

    if (response.status === 401 && !retried) {
      try {
        await this.refreshToken();
        return this.sendPrivateRequest(url, options, true);
      } catch {
        removeFromLocalStorage(LOGGED_IN_USER_INFO);
        window.location.reload();
        throw new HttpErrors(extractError(results), response.status);
      }
    }

    if (response.status >= 400) {
      throw new HttpErrors(extractError(results), response.status);
    }

    notifyOnPartialContent(response.status, results);

    const rawMeta = results.metadata ?? results.meta;
    return {
      data: results.data,
      meta: normalizeMeta(rawMeta),
      status: response.status,
    };
  };

  // ---------------- API HELPERS ----------------

  getPublicData(url, options = {}) {
    return this.sendPublicRequest(url, options);
  }

  getData = async (url, options = {}) => {
    return this.sendPrivateRequest(url, options);
  };

  putData = async (url, body) => {
    return this.sendPrivateRequest(url, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  };

  postData = async (url, body) => {
    return this.sendPrivateRequest(url, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  };

  postDataWithCustomHeader = async (url, body, customHeaders = {}) => {
    return this.sendPrivateRequest(url, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  };

  deleteData = async (url, body) => {
    return this.sendPrivateRequest(url, {
      method: 'DELETE',
      body: JSON.stringify(body),
    });
  };
}

export const createFetcher = () => Fetcher.getInstance();
